import { 
  Project, 
  ProjectDetailResponse, 
  ProjectGraphResponse,
  ProjectGraphData,
  ProjectUnlockDataResponse,
  RFQRequest,
  RFQResponseData,
  ProjectsStatsResponse
} from '@/types/project';
import { BaseService } from './api';

interface ProjectResponse {
  page: number;
  items: number;
  total: number;
  total_pages?: number;
  data: Project[];
}

interface PaginationParams {
  limit?: number;
  offset?: number;
  search?: string;
  sort_by?: 'DATE' | 'PROJECT_NAME' | 'ORDERS';
  sort_direction?: 'ASC' | 'DESC';
  include_rounds?: boolean;
  include_project_stats?: boolean;
}

export class DataRoomService extends BaseService {
  async getFundingRounds(params?: PaginationParams): Promise<ProjectResponse> {
    const queryParams = {
      limit: params?.limit || 10,
      offset: params?.offset || 0,
      include_project_stats: params?.include_project_stats !== false ? 'true' : 'false',
      include_rounds: params?.include_rounds !== false ? 'true' : 'false',
      sort_by: params?.sort_by || 'ORDERS',
      sort_direction: params?.sort_direction || 'DESC',
      ...(params?.search ? { search: params.search } : {}),
    };
    const response = await this.get<ProjectResponse>('/projects', { params: queryParams });
    return response;
  }

  async getProjectsStats(): Promise<ProjectsStatsResponse> {
    const response = await this.get<{ data: ProjectsStatsResponse }>('/projects/stats');
    return response.data;
  }

  async getUnlocksStats(): Promise<ProjectsStatsResponse> {
    const response = await this.get<{ data: ProjectsStatsResponse }>('/projects/unlocks/stats');
    return response.data;
  }

  // Project detail methods
  async getProjectDetail(projectId: string, includeProjectStats: boolean = true): Promise<Project> {
    const queryParams = {
      include_project_stats: includeProjectStats ? 'true' : 'false',
    };
    const response = await this.get<ProjectDetailResponse>(`/projects/${projectId}`, { params: queryParams });
    return response.data;
  }

  async getProjectUnlocks(projectId: string): Promise<ProjectUnlockDataResponse | null> {
    try {
      const response = await this.get<ProjectUnlockDataResponse>(`/projects/${projectId}/unlock`);
      return response;
    } catch (error: any) {
      // If the error is 404 (Project unlock data not found), return null instead of throwing
      // Check both the error message and status code
      const errorMessage = error?.message || '';
      const statusCode = error?.status || error?.response?.status;
      
      if (statusCode === 404 || errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        console.log('Project unlock data not found (404), returning null');
        return null;
      }
      // For other errors, re-throw to be handled by the caller
      throw error;
    }
  }

  async getProjectGraph(projectId: string): Promise<ProjectGraphData> {
    const response = await this.get<ProjectGraphResponse>(`/projects/${projectId}/graph`);
    
    // Extract data from response, similar to getProjectDetail
    // The API returns { data: ProjectGraphData }
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data as ProjectGraphData;
    }
    
    // Fallback: if response is already the data (no wrapper)
    if (response && typeof response === 'object') {
      return response as unknown as ProjectGraphData;
    }
    
    throw new Error('Invalid graph data response structure');
  }

  async createRFQ(rfqData: RFQRequest): Promise<RFQResponseData> {
    const response = await this.post<RFQResponseData>('/rfq', rfqData);
    return response;
  }
}

export const dataRoomService = new DataRoomService(); 